import React from "react";
import { getFileExtension } from "lib/actions/files";
import { IngressListItemModel } from "./lib/IngressListItemModel";
import { UploadStatus } from "lib/types/UploadStatus";
import { AssetIngressModel } from "lib/types/assets/AssetIngressModel";
import { AssetCategory } from "lib/types/assets/AssetCategory";
import PauseSvg from "components/icons/24/actions/action-pause.svg";
import CancelSvg from "components/icons/24/actions/action-close-circle.svg";
import CrossSvg from "components/icons/24/actions/action-close.svg";
import Card from "components/elements/Card/Card";
import SpanNoWrap from "components/elements/Text/SpanNoWrap";
import ProgressBarDeterminate from "components/elements/Loading/ProgressBarDeterminate";
import ProgressBarIndeterminate from "components/elements/Loading/ProgressBarIndeterminate";
import IngressStatusChip from "components/elements/Chip/IngressStatusChip";
import AssetTypeIcon from "components/elements/Chip/AssetTypeIcon";

export default function IngressListItem(p: {
  item: IngressListItemModel;
  onPause?(): void;
  onResume?(): void;
  onRetry?(): void;
  onCancel?(): void;
  onDismiss?(): void;
}) {
  const displayStatus = getDisplayStatus(p.item);
  const progressPercent = getProgressValue(p.item);
  const actions = getActions(p.item);
  const progressType = getProgressBarType(p.item);
  const progressColorClasses = getProgressTailwindClasses(p.item);
  const errorMessage = p.item.errorMessage;

  return (
    <Card>
      <div className="relative pb-3 pt-2">
        <div className="flex items-stretch pb-3">
          <div className="-mt-1 flex-grow flex-row self-center overflow-hidden px-3 py-2">
            <div className="mt-1 flex items-center space-x-3 whitespace-nowrap">
              <IngressTitle
                name={p.item.name}
                ingressType={p.item.ingressSource}
              />

              <div className="flex items-center space-x-2">
                {actions.has("retry") && (
                  <div onClick={p.onRetry}>
                    <p className="mr-2 cursor-pointer text-3xs">Retry</p>
                  </div>
                )}

                <IngressStatusChip
                  status={displayStatus}
                  progressPercent={progressPercent}
                />

                {actions.has("resume") && (
                  <div onClick={p.onResume}>
                    <PauseSvg className="h-5 cursor-pointer text-icon-normal hover:text-icon-hover" />
                  </div>
                )}
                {actions.has("pause") && (
                  <div onClick={p.onPause}>
                    <PauseSvg className="h-5 cursor-pointer text-icon-normal hover:text-icon-hover" />
                  </div>
                )}
                {actions.has("cancel") && (
                  <div onClick={p.onCancel}>
                    <CancelSvg className="h-5 cursor-pointer text-icon-normal hover:text-red-600" />
                  </div>
                )}
                {actions.has("dismiss") && (
                  <div onClick={p.onDismiss}>
                    <CrossSvg className="h-5 cursor-pointer text-icon-normal hover:text-icon-hover" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-3 left-0 right-0 mx-3 h-0.5">
          {progressType === "determinate" && (
            <ProgressBarDeterminate
              progressPercent={progressPercent}
              progressColorClass={progressColorClasses.foreground}
            />
          )}
          {progressType === "indeterminate" && (
            <ProgressBarIndeterminate
              progressColorClass={progressColorClasses.foreground}
              bgColorClass={progressColorClasses.background}
            />
          )}
        </div>
      </div>
      {errorMessage && (
        <div>
          {(p.item.ingressStatus === "error" ||
            p.item.ingressStatus === "partialError") && (
            <div className="border-t-solid mx-3 border-t-2 border-red-200 pt-3" />
          )}
          <div
            className={
              p.item.ingressStatus === "error" ||
              p.item.ingressStatus === "partialError"
                ? "m-3 flex items-center justify-between rounded-md bg-red-50 px-6 py-3 text-xs text-red-500"
                : "m-3 flex items-center justify-between rounded-md bg-neutral-50 px-6 py-3 text-xs text-secondary"
            }
          >
            <div className="flex flex-wrap">{errorMessage}</div>
          </div>
        </div>
      )}
    </Card>
  );
}

function IngressTitle(p: {
  name: string;
  ingressType: IngressListItemModel["ingressSource"];
}) {
  const extension = getFileExtension(p.name);
  const name =
    (extension != null && p.name.slice(0, -extension.length - 1)) || p.name;

  const iconPart = (
    <div className="min-w-max">
      {p.ingressType === "browserUpload" ? (
        <AssetTypeIcon ext={extension} />
      ) : (
        <AssetTypeIcon category={AssetCategory.bim} />
      )}
    </div>
  );

  const namePart = (
    <>
      <div className="ml-2 flex h-5 min-w-0 truncate whitespace-nowrap text-nowrap">
        <SpanNoWrap>{name.slice(0, -3)}</SpanNoWrap>
      </div>
      {name.slice(-3)}
    </>
  );

  const extPart = p.ingressType !== "speckleImport" && !!extension && (
    <>.{extension}</>
  );

  return (
    <div className="flex min-w-0 flex-grow text-sm text-gray-900">
      {iconPart}
      {namePart}
      {extPart}
    </div>
  );
}

type Action = "pause" | "resume" | "cancel" | "retry" | "dismiss";

const statusActionsMap = new Map<
  UploadStatus | AssetIngressModel["status"],
  readonly Action[]
>([
  ["none", []],
  [UploadStatus.preparing, []],
  [UploadStatus.queued, ["cancel"]],
  [UploadStatus.paused, ["resume", "cancel"]],
  [UploadStatus.uploading, ["pause", "cancel"]],
  [UploadStatus.error, ["retry", "cancel"]],
  ["awaitingProcessing", []],
  ["processing", []],
  ["done", ["dismiss"]],
  ["error", ["dismiss"]],
  ["partialError", ["dismiss"]],
]);

function getActions(ingress: IngressListItemModel) {
  const actions =
    statusActionsMap.get(ingress.uploadStatus) ??
    statusActionsMap.get(ingress.ingressStatus) ??
    statusActionsMap.get("none");
  return new Set(actions);
}

function getDisplayStatus(ingress: IngressListItemModel) {
  if (
    ingress.ingressStatus === "awaitingProcessing" ||
    ingress.ingressStatus == "processing" ||
    ingress.ingressStatus === "done" ||
    ingress.ingressStatus === "error" ||
    ingress.ingressStatus === "partialError"
  ) {
    return ingress.ingressStatus;
  } else if (ingress.uploadStatus !== null) {
    return ingress.uploadStatus;
  }
  return UploadStatus.none;
}

function getProgressBarType(ingress: IngressListItemModel) {
  if (
    ingress.uploadStatus === UploadStatus.uploading ||
    ingress.uploadStatus === UploadStatus.paused ||
    ingress.ingressStatus === "done"
  ) {
    return "determinate" as const;
  } else if (
    ingress.ingressStatus === "processing" ||
    ingress.uploadStatus === UploadStatus.preparing
  ) {
    return "indeterminate" as const;
  }
  return "none" as const;
}

function getProgressTailwindClasses(ingress: IngressListItemModel) {
  let f: string, b: string;
  if (ingress.uploadStatus === UploadStatus.preparing) {
    b = "bg-purple-50";
    f = "bg-purple-600";
  } else if (ingress.uploadStatus === UploadStatus.queued) {
    f = "bg-purple-600";
  } else if (ingress.uploadStatus === UploadStatus.paused) {
    f = "bg-gray-400";
  } else if (ingress.uploadStatus === UploadStatus.uploading) {
    f = "bg-blue-600";
  } else if (ingress.ingressStatus === "processing") {
    f = "bg-orange-400";
  } else if (
    ingress.ingressStatus === "done" ||
    ingress.uploadStatus === UploadStatus.complete
  ) {
    f = "bg-kf-legacy-green-400";
  }
  return {
    foreground: f ?? "",
    background: b ?? "",
  } as const;
}

function getProgressValue(ingress: IngressListItemModel) {
  return ingress.ingressStatus === "done"
    ? 100
    : (ingress.progress && Math.round(ingress.progress * 100)) || 0;
}
